import { Controller } from "@hotwired/stimulus"

// The right panel's "Table of Contents" / "Review Activity" tab strip.
//
// The server picks the initial tab (review mode defaults to Review Activity), so
// this controller only handles switching and persisting. The cookie is written the
// same way course-builder-outline writes its collapse cookie — same max-age, path
// and SameSite — so the settings frame re-renders on the chosen tab after a refresh.
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365

export default class extends Controller {
  static targets = ["tab", "panel"]
  static values = {
    cookieName: { type: String, default: "courseBuilderSettingsTab" }
  }

  select(event) {
    this.activate(event.currentTarget.dataset.tabKey)
  }

  // Roving tabindex: only the active tab is reachable by Tab, and Left/Right move
  // between them once focus is inside the strip.
  navigate(event) {
    const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key]
    if (!step) return

    event.preventDefault()

    const tabs = this.tabTargets
    const currentIndex = tabs.indexOf(event.currentTarget)
    const nextTab = tabs[(currentIndex + step + tabs.length) % tabs.length]

    this.activate(nextTab.dataset.tabKey)
    nextTab.focus()
  }

  activate(tabKey) {
    if (!tabKey) return

    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.tabKey === tabKey
      tab.classList.toggle("is-active", active)
      tab.setAttribute("aria-selected", active ? "true" : "false")
      tab.tabIndex = active ? 0 : -1
    })

    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.tabKey === tabKey
      panel.hidden = !active
      if (active) this.loadLazyFrames(panel)
    })

    this.writeCookie(tabKey)
  }

  // An empty lazy turbo-frame sitting at the panel's edge does not reliably trip its
  // own IntersectionObserver when the panel un-hides, so nudge any not-yet-loaded frame
  // to fetch the moment its tab is shown. Already-loaded frames (`[complete]`) are left
  // alone, so this only ever adds a load — it can't re-fetch a panel that is up to date.
  loadLazyFrames(panel) {
    panel.querySelectorAll("turbo-frame[src][loading='lazy']:not([complete])").forEach((frame) => {
      frame.setAttribute("loading", "eager")
    })
  }

  writeCookie(tabKey) {
    document.cookie = `${encodeURIComponent(this.cookieNameValue)}=${tabKey};max-age=${COOKIE_MAX_AGE};path=/;SameSite=Lax`
  }
}
